import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileQuestion, FileText, HelpCircle, MessageCircle, Compass } from "lucide-react";
import { openSupportChat } from "@/utils/openSupportChat";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Same reveal hook the marketing pages use (threshold 0.05 per DESIGN.md)
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

export default function NotFound() {
  const navigate = useNavigate();
  const [heroRef, heroInView] = useInView();
  const [cardsRef, cardsInView] = useInView();

  const quickLinks = [
    {
      icon: FileText,
      title: "Create a document",
      text: "Paystubs, tax forms, resumes, and more, done in minutes.",
      onClick: () => navigate("/app"),
      cls: "bg-slate-100",
      titleCls: "text-slate-900",
      textCls: "text-slate-600",
      circleCls: "bg-white",
      iconCls: "text-slate-900",
      arrowCls: "text-slate-900",
    },
    {
      icon: HelpCircle,
      title: "Browse the FAQ",
      text: "Answers to the questions our customers ask the most.",
      onClick: () => navigate("/faq"),
      cls: "bg-emerald-400",
      titleCls: "text-emerald-950",
      textCls: "text-emerald-900/80",
      circleCls: "bg-white",
      iconCls: "text-emerald-700",
      arrowCls: "text-emerald-950",
    },
    {
      icon: MessageCircle,
      title: "Chat with us",
      text: "Our support team is around 24/7 if you're stuck.",
      onClick: () => openSupportChat(),
      cls: "bg-[#0b0b0b]",
      titleCls: "text-white",
      textCls: "text-slate-400",
      circleCls: "bg-white/10",
      iconCls: "text-emerald-400",
      arrowCls: "text-white",
    },
  ];

  return (
    <div className="min-h-screen bg-white relative flex flex-col" style={{ overflowX: "clip" }}>
      <div className="noise-overlay" />

      <Header title="MintSlip" />

      <div className="flex-1">
        {/* Hero — giant display 404, the zero drawn as a floating emerald coin */}
        <section ref={heroRef} className="max-w-[1288px] mx-auto px-6 pt-14 pb-10 md:pt-20 md:pb-14">
          <div className={`text-center transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 rounded-full border border-emerald-200/80 text-sm font-medium text-emerald-900 mb-8">
              <Compass className="w-4 h-4 text-emerald-700" />
              Error 404
            </div>

            <div className="flex items-center justify-center gap-2 sm:gap-4 mb-8 select-none" aria-label="404">
              <span className="font-display font-black text-slate-900 leading-none text-[110px] sm:text-[170px] md:text-[200px]" aria-hidden="true">4</span>
              <span
                className="nf-coin rounded-full bg-emerald-400 flex items-center justify-center shadow-[0_18px_45px_rgba(16,185,129,0.35)] w-[92px] h-[92px] sm:w-[142px] sm:h-[142px] md:w-[168px] md:h-[168px]"
                aria-hidden="true"
              >
                <span className="rounded-full bg-white flex items-center justify-center w-[56px] h-[56px] sm:w-[86px] sm:h-[86px] md:w-[102px] md:h-[102px]">
                  <FileQuestion className="text-emerald-600 w-7 h-7 sm:w-11 sm:h-11 md:w-12 md:h-12" strokeWidth={1.8} />
                </span>
              </span>
              <span className="font-display font-black text-slate-900 leading-none text-[110px] sm:text-[170px] md:text-[200px]" aria-hidden="true">4</span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
              This page went <span className="font-black">missing</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
              The page you're looking for doesn't exist or has been moved. Let's get you back to something useful.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button className="navbar-cta" onClick={() => navigate("/")}>
                <FileText size={18} />
                <span>Back to home</span>
              </button>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:border-slate-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Go back
              </button>
            </div>
          </div>

          {/* Gentle float for the coin */}
          <style>{`
            .nf-coin { animation: nfFloat 3.6s ease-in-out infinite; }
            @keyframes nfFloat {
              0%, 100% { transform: translateY(0) rotate(-4deg); }
              50% { transform: translateY(-14px) rotate(4deg); }
            }
            @media (prefers-reduced-motion: reduce) {
              .nf-coin { animation: none; }
            }
          `}</style>
        </section>

        {/* Quick links — the home bento tones */}
        <section ref={cardsRef} className="max-w-[1288px] mx-auto px-6 pb-20 md:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {quickLinks.map((link, i) => (
              <button
                key={link.title}
                onClick={link.onClick}
                className={`${link.cls} rounded-[28px] p-7 md:p-8 text-left group hover:-translate-y-1 transition-all duration-700 ${cardsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: cardsInView ? `${i * 100}ms` : "0ms" }}
              >
                <span className={`${link.circleCls} w-12 h-12 rounded-full shadow-sm flex items-center justify-center mb-5`}>
                  <link.icon className={`w-6 h-6 ${link.iconCls}`} />
                </span>
                <span className={`font-display text-xl font-bold tracking-tight block mb-1.5 ${link.titleCls}`}>
                  {link.title}
                </span>
                <span className={`text-sm leading-relaxed block mb-4 ${link.textCls}`}>
                  {link.text}
                </span>
                <ArrowRight className={`w-5 h-5 ${link.arrowCls} transition-transform group-hover:translate-x-1`} />
              </button>
            ))}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
